/**
 * <axiom-predictive-search>: the search panel's field, its content before
 * typing and the predictive results. header.js opens and closes the panel
 * and dispatches search:open on it.
 *
 * Before typing: rolling suggestions in the empty field and recent searches
 * (kept on the device). While typing: results
 * from /search/suggest rendered by the predictive-search section, filtered
 * by tabs, with the typed words highlighted and arrow-key navigation.
 */
(function () {
  'use strict';

  if (customElements.get('axiom-predictive-search')) return;

  var RECENT_KEY = 'axiom_recent_searches';

  function read(key) {
    try {
      var list = JSON.parse(localStorage.getItem(key) || '[]');
      return Array.isArray(list) ? list : [];
    } catch (error) {
      return [];
    }
  }

  function write(key, value) {
    // Kept only when the visitor allows preferences, if the store asks.
    var privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (privacy && typeof privacy.preferencesProcessingAllowed === 'function' && !privacy.preferencesProcessingAllowed()) return;
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      /* Storage unavailable. */
    }
  }

  function escapeHTML(value) {
    return String(value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function debounce(fn, wait) {
    var timer;
    return function () {
      var args = arguments;
      var self = this;
      clearTimeout(timer);
      timer = setTimeout(function () { fn.apply(self, args); }, wait);
    };
  }

  function routes() {
    var r = (window.Axiom && window.Axiom.routes) || {};
    var root = (r.root || (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/').replace(/\/$/, '');
    return { root: root, suggest: r.predictive_search || root + '/search/suggest' };
  }

  function reducedMotion() {
    return window.Axiom && typeof window.Axiom.prefersReducedMotion === 'function'
      ? window.Axiom.prefersReducedMotion()
      : window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  class AxiomPredictiveSearch extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.input = this.querySelector('[data-ps-input]');
      this.output = this.querySelector('[data-ps-output]');
      this.idle = this.querySelector('[data-ps-idle]');
      this.form = this.querySelector('[data-ps-form]');
      this.clearButton = this.querySelector('[data-ps-clear]');
      this.ghost = this.querySelector('[data-ps-ghost]');
      this.status = this.querySelector('[data-ps-status]');
      this.cache = {};
      this.enabled = this.dataset.enabled !== 'false';
      if (!this.input || !this.output) return;

      this.onInput = debounce(this.onInput.bind(this), 200);
      this.input.addEventListener('input', () => {
        this.syncField();
        this.onInput();
      });
      this.input.addEventListener('keydown', this.onKeydown.bind(this));
      if (this.clearButton) {
        this.clearButton.addEventListener('click', () => {
          this.input.value = '';
          this.syncField();
          this.show(false);
          this.input.focus();
        });
      }
      if (this.form) {
        this.form.addEventListener('submit', () => this.remember(this.input.value));
      }

      // Results: tabs and remembering the term when a suggestion is used.
      this.output.addEventListener('click', (event) => {
        var tab = event.target.closest('[data-ps-tab]');
        if (tab) {
          this.filter(tab.dataset.psTab);
          return;
        }
        var term = event.target.closest('[data-ps-term], [data-ps-submit-term]');
        if (term) this.remember(term.dataset.psTerm || term.dataset.psSubmitTerm);
      });

      var panel = this.closest('[data-search-drawer]');
      if (panel) {
        panel.addEventListener('search:open', () => {
          this.hydrateDeferred();
          this.renderRecent();
          this.startGhost();
        });
      }

      var clearRecent = this.querySelector('[data-ps-recent-clear]');
      if (clearRecent) {
        clearRecent.addEventListener('click', () => {
          write(RECENT_KEY, []);
          this.renderRecent();
        });
      }

      this.syncField();
    }

    disconnectedCallback() {
      clearInterval(this.ghostTimer);
      clearTimeout(this.ghostLeave);
      this.ghostTimer = null;
    }

    /* ---------- Empty field: rolling suggestions ---------- */
    syncField() {
      var empty = !this.input.value;
      if (this.clearButton) this.clearButton.hidden = empty;
      if (this.ghost) {
        this.ghost.hidden = !empty;
        this.input.classList.toggle('has-ghost', empty);
      }
    }

    startGhost() {
      if (!this.ghost || this.ghostTimer || reducedMotion()) return;
      var words = Array.from(this.ghost.querySelectorAll('[data-ps-word]'));
      if (words.length < 2) return;
      var index = 0;
      // Sends a word that has left back to its waiting place below with no
      // transition, so it never glides back down across the field.
      var park = (word) => {
        if (word.classList.contains('is-active')) return;
        word.style.transition = 'none';
        word.classList.remove('is-leaving');
        void word.offsetHeight;
        word.style.transition = '';
      };
      this.ghostTimer = setInterval(() => {
        if (!this.isConnected) return clearInterval(this.ghostTimer);
        var current = words[index];
        index = (index + 1) % words.length;
        var incoming = words[index];
        park(incoming);
        current.classList.remove('is-active');
        current.classList.add('is-leaving');
        incoming.classList.add('is-active');
        clearTimeout(this.ghostLeave);
        this.ghostLeave = setTimeout(() => park(current), 700);
      }, 2400);
    }

    /* ---------- Content kept out of the closed panel (product images) ---------- */
    hydrateDeferred() {
      this.querySelectorAll('template[data-ps-deferred]').forEach((template) => {
        template.replaceWith(template.content);
      });
    }

    /* ---------- Recent searches ---------- */
    remember(term) {
      term = (term || '').trim();
      if (!term || this.dataset.recent !== 'true') return;
      var list = read(RECENT_KEY).filter((item) => String(item).toLowerCase() !== term.toLowerCase());
      list.unshift(term);
      write(RECENT_KEY, list.slice(0, 6));
    }

    renderRecent() {
      var box = this.querySelector('[data-ps-recent]');
      if (!box) return;
      var list = read(RECENT_KEY);
      var base = routes().root + '/search?q=';
      box.querySelector('[data-ps-recent-list]').innerHTML = list
        .map((term) => '<li><a class="search-list__link" href="' + base + encodeURIComponent(term) + '&options%5Bprefix%5D=last">' + escapeHTML(term) + '</a></li>')
        .join('');
      box.hidden = list.length === 0;
    }

    /* ---------- Results ---------- */
    onInput() {
      var term = this.input.value.trim();
      if (!term || !this.enabled) {
        this.show(false);
        return;
      }
      if (this.cache[term]) {
        this.render(this.cache[term], term);
        return;
      }
      if (this.request) this.request.abort();
      this.request = new AbortController();
      this.classList.add('is-loading');
      var url =
        routes().suggest +
        '?q=' + encodeURIComponent(term) +
        '&section_id=' + encodeURIComponent(this.dataset.sectionId || 'predictive-search') +
        '&resources[type]=' + encodeURIComponent(this.dataset.resources || 'product,collection,article,page,query') +
        '&resources[limit]=' + (Number(this.dataset.limit) || 8) +
        '&resources[options][unavailable_products]=last';
      fetch(url, { signal: this.request.signal })
        .then(function (r) {
          if (!r.ok) throw new Error(r.status);
          return r.text();
        })
        .then((html) => {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var results = doc.querySelector('[data-ps-results]');
          var markup = results ? results.outerHTML : '';
          this.cache[term] = markup;
          if (this.input.value.trim() === term) this.render(markup, term);
        })
        .catch(function () {})
        .finally(() => this.classList.remove('is-loading'));
    }

    render(markup, term) {
      this.output.innerHTML = markup;
      this.highlight(term);
      this.show(true);
      this.collectOptions();
      var statusText = this.output.querySelector('[data-ps-status-text]');
      this.announce(statusText ? statusText.textContent.trim() : '');
    }

    // The status line is the only live region: it reads the result count, not the results.
    announce(text) {
      if (this.status) this.status.textContent = text;
    }

    highlight(term) {
      var words = term.split(/\s+/).filter((w) => w.length > 1).map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
      if (!words.length) return;
      var pattern = new RegExp('(' + words.join('|') + ')', 'ig');
      this.output.querySelectorAll('[data-ps-title]').forEach((el) => {
        el.innerHTML = escapeHTML(el.textContent).replace(pattern, '<mark>$1</mark>');
      });
    }

    // Options get stable ids once per render; the active one is cleared
    // whenever the list changes, so aria-activedescendant never points at a
    // removed or hidden option.
    collectOptions() {
      this.options = Array.from(this.output.querySelectorAll('[role="option"]'));
      this.options.forEach((option, index) => {
        option.id = 'SearchDrawerOption-' + index;
        option.classList.remove('is-active');
        option.setAttribute('aria-selected', 'false');
      });
      this.active = -1;
      this.input.removeAttribute('aria-activedescendant');
    }

    filter(type) {
      var results = this.output.querySelector('[data-ps-results]');
      if (!results) return;
      results.dataset.filter = type;
      results.querySelectorAll('[data-ps-tab]').forEach((tab) => tab.setAttribute('aria-pressed', String(tab.dataset.psTab === type)));
      this.collectOptions();
    }

    show(open) {
      this.output.hidden = !open;
      if (this.idle) this.idle.hidden = open;
      this.input.setAttribute('aria-expanded', String(open));
      if (!open) {
        this.output.innerHTML = '';
        this.options = [];
        this.active = -1;
        this.input.removeAttribute('aria-activedescendant');
        this.announce('');
      }
      this.dispatchEvent(new CustomEvent('search:results', { bubbles: true }));
    }

    onKeydown(event) {
      if (!this.options || !this.options.length) return;
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp' && event.key !== 'Enter') return;
      var visible = this.options.filter((el) => el.offsetParent !== null);
      if (!visible.length) return;
      if (event.key === 'Enter') {
        if (this.active > -1 && visible[this.active]) {
          event.preventDefault();
          visible[this.active].click();
        }
        return;
      }
      event.preventDefault();
      var step = event.key === 'ArrowDown' ? 1 : -1;
      this.active = (this.active + step + visible.length) % visible.length;
      visible.forEach((option, i) => {
        var on = i === this.active;
        option.classList.toggle('is-active', on);
        option.setAttribute('aria-selected', String(on));
        if (on) {
          this.input.setAttribute('aria-activedescendant', option.id);
          option.scrollIntoView({ block: 'nearest' });
        }
      });
    }
  }

  customElements.define('axiom-predictive-search', AxiomPredictiveSearch);
})();
